import { useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { PaymentNameFormDefault } from 'components/pages/Payment/PaymentNameFormDefault';
import { PaymentStatus } from 'components/pages/Payment/PaymentStatus';
import { Button, PulseWaveLoader, Text, VStack } from 'components/uikit';
import { useFetchPayment } from 'data/payments/hooks';
import { trackEvent } from 'utils/analytics';
import { AnalyticsEvents } from 'utils/analytics/events/AnalyticsEventType';
import { processDeeplinksWithReload } from 'utils/openDeeplink';
import { resolve } from 'utils/remote';

import { PaymentCredentials } from './PaymentCredentials';
import { useChatwoot } from './useChatwoot';

export type PaymentPageStyle = '1xbet' | 'default' | 'riobet';

interface PaymentPageProps {
  depositId: string;
  style?: PaymentPageStyle;
}

export function PaymentPage({
  depositId,
  style = 'default',
}: PaymentPageProps) {
  const { t } = useTranslation('payments');
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [showChatwoot, setShowChatwoot] = useState(false);

  const { paymentRemote, fetchPayment } = useFetchPayment(depositId);
  const merchantType = style;

  useEffect(() => {
    processDeeplinksWithReload(depositId);
  }, [depositId]);

  useChatwoot(depositId, showChatwoot);

  return (
    <VStack
      spacing={['32px', '16px']}
      minH={['100%', 'auto']}
      maxH={['none', '100vh']}
      sx={{ justifyContent: ['center', 'safe center'] }}
    >
      {resolve(paymentRemote, {
        LOADING: () => (
          <PulseWaveLoader
            size={[300, 700]}
            color={style === 'riobet' ? '237, 201, 127' : '0, 150, 255'}
          />
        ),
        ERROR: (errorMessage) => {
          trackEvent(AnalyticsEvents.PAYMENT_ERROR, {
            order_id: depositId,
            error_type: 'network',
            error_message: errorMessage,
          });

          return (
            <>
              <Text
                textAlign='center'
                as='h3'
                fontSize='2xl'
                fontWeight='medium'
                color='error.text'
              >
                {t('payment_fetching_error')}
              </Text>
              <Button
                w='360px'
                size='lg'
                variant='paymentOutline'
                onClick={fetchPayment}
              >
                {t('retry')}
              </Button>
            </>
          );
        },
        SUCCESS: (payment) => {
          if (payment.showChatwoot && !showChatwoot) {
            setShowChatwoot(true);
          }

          if (isFirstLoad) {
            if (payment && payment.amount != null) {
              trackEvent(AnalyticsEvents.PAYMENT_PAGE_LOADED, {
                order_id: depositId,
                payment_method: payment.method,
                amount_base: payment.amount,
                amount_local: payment.crossborderAmount ?? '',
                local_currency: payment.crossborderCurrency ?? '',
              });
              setIsFirstLoad(false);
            }
          }

          return payment.status !== 'CREATED' || payment.amount == null ? (
            <>
              <PaymentStatus
                status={payment.status}
                successRedirectLink={payment.successRedirect}
                failureRedirectLink={payment.failureRedirect}
              />
            </>
          ) : payment.credentials ? (
            <PaymentCredentials
              id={depositId}
              acquirer={payment.acquirer}
              method={payment.method}
              amount={payment.amount}
              currency={payment.currency}
              expiryDate={payment.expiryDate}
              credentials={payment.credentials}
              failureRedirectLink={payment.failureRedirect}
              customerId={payment.customerId}
              style={merchantType}
              fromBank={payment.fromBank}
              status={payment.status}
              crossborderAmount={payment.crossborderAmount ?? null}
              crossborderCurrency={payment.crossborderCurrency ?? null}
              showChatwoot={showChatwoot}
            />
          ) : (
            <PaymentNameFormDefault
              depositId={depositId}
              amount={payment.amount}
            />
          );
        },
      })}
    </VStack>
  );
}
